import { PublicApiError, logoutMemberSession, refreshMemberSession, type MemberDashboard, type PublicAuthUser } from "./api";

const MEMBER_ACCESS_TOKEN_STORAGE_KEY = "asc-member-access-token";
const MEMBER_USER_STORAGE_KEY = "asc-member-user";
const MEMBER_DASHBOARD_STORAGE_KEY = "asc-member-dashboard";
const MEMBER_MANUAL_LOGOUT_STORAGE_KEY = "asc-member-manual-logout";
const MEMBER_SESSION_EVENT_NAME = "asc-member-session-change";

function canUseStorage() {
  return typeof window !== "undefined";
}

function isMemberUser(user: PublicAuthUser | null | undefined): user is PublicAuthUser {
  return Boolean(user && user.role === "MEMBER");
}

function isUnauthorizedError(caughtError: unknown): caughtError is PublicApiError {
  return caughtError instanceof PublicApiError && (caughtError.status === 401 || caughtError.status === 403);
}

function isRefreshSessionError(caughtError: unknown): caughtError is PublicApiError {
  return caughtError instanceof PublicApiError && (caughtError.status === 400 || caughtError.status === 401 || caughtError.status === 403);
}

export function readMemberAccessToken() {
  if (!canUseStorage()) {
    return null;
  }

  return localStorage.getItem(MEMBER_ACCESS_TOKEN_STORAGE_KEY);
}

function hasManualLogoutMarker() {
  if (!canUseStorage()) {
    return false;
  }

  return localStorage.getItem(MEMBER_MANUAL_LOGOUT_STORAGE_KEY) === "1";
}

export function hasMemberAccessToken() {
  return Boolean(readMemberAccessToken() && readStoredMemberUser());
}

export function readStoredMemberUser(): PublicAuthUser | null {
  if (!canUseStorage()) {
    return null;
  }

  const rawValue = localStorage.getItem(MEMBER_USER_STORAGE_KEY);

  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as PublicAuthUser;
    const normalizedUser = {
      ...parsed,
      mustChangePassword: Boolean(parsed.mustChangePassword)
    } satisfies PublicAuthUser;

    if (!isMemberUser(normalizedUser)) {
      localStorage.removeItem(MEMBER_USER_STORAGE_KEY);
      localStorage.removeItem(MEMBER_ACCESS_TOKEN_STORAGE_KEY);
      localStorage.removeItem(MEMBER_DASHBOARD_STORAGE_KEY);
      return null;
    }

    return normalizedUser;
  } catch {
    return null;
  }
}

function readStoredJsonValue<T>(storageKey: string): T | null {
  if (!canUseStorage()) {
    return null;
  }

  const rawValue = localStorage.getItem(storageKey);

  if (!rawValue) {
    return null;
  }

  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return null;
  }
}

function emitMemberSessionChange() {
  if (!canUseStorage()) {
    return;
  }

  window.dispatchEvent(new CustomEvent(MEMBER_SESSION_EVENT_NAME));
}

function decodeBase64Url(value: string) {
  if (typeof atob !== "function") {
    return null;
  }

  const normalizedValue = value.replace(/-/g, "+").replace(/_/g, "/");
  const paddingLength = (4 - (normalizedValue.length % 4 || 4)) % 4;
  const paddedValue = normalizedValue.padEnd(normalizedValue.length + paddingLength, "=");

  return atob(paddedValue);
}

function readAccessTokenExpiration(token: string) {
  const payloadSegment = token.split(".")[1];

  if (!payloadSegment) {
    return null;
  }

  try {
    const payload = JSON.parse(decodeBase64Url(payloadSegment) ?? "{}") as { exp?: number };
    return typeof payload.exp === "number" ? payload.exp * 1000 : null;
  } catch {
    return null;
  }
}

export function isMemberAccessTokenExpired(token: string, clockSkewSeconds = 30) {
  const expiration = readAccessTokenExpiration(token);

  if (!expiration) {
    return false;
  }

  return Date.now() + clockSkewSeconds * 1000 >= expiration;
}

async function refreshMemberSessionSilently() {
  try {
    const refreshed = await refreshMemberSession();

    if (!isMemberUser(refreshed.user)) {
      return;
    }

    persistMemberSession(refreshed.accessToken, refreshed.user);
  } catch {
    // Keep the current session while the existing access token is still valid.
  }
}

export function readStoredMemberDashboard(): MemberDashboard | null {
  return readStoredJsonValue<MemberDashboard>(MEMBER_DASHBOARD_STORAGE_KEY);
}

export function persistMemberDashboardSnapshot(dashboard: MemberDashboard) {
  if (!canUseStorage()) {
    return;
  }

  localStorage.setItem(MEMBER_DASHBOARD_STORAGE_KEY, JSON.stringify(dashboard));
  emitMemberSessionChange();
}

export function subscribeToMemberSessionChanges(listener: () => void) {
  if (!canUseStorage()) {
    return () => undefined;
  }

  window.addEventListener(MEMBER_SESSION_EVENT_NAME, listener);
  return () => window.removeEventListener(MEMBER_SESSION_EVENT_NAME, listener);
}

export function readMemberDisplayName() {
  const user = readStoredMemberUser();

  if (!user) {
    return null;
  }

  const fullName = [user.prenom, user.nom].filter(Boolean).join(" ").trim();
  return fullName || "Membre";
}

export function persistMemberSession(accessToken: string, user: PublicAuthUser) {
  if (!canUseStorage()) {
    return;
  }

  if (!isMemberUser(user)) {
    clearMemberSession();
    return;
  }

  const storedUser = readStoredMemberUser();
  const nextUser = storedUser
    ? {
        ...storedUser,
        ...user,
        profil: user.profil ?? storedUser.profil ?? null
      }
    : user;

  localStorage.setItem(MEMBER_ACCESS_TOKEN_STORAGE_KEY, accessToken);
  localStorage.setItem(MEMBER_USER_STORAGE_KEY, JSON.stringify(nextUser));
  localStorage.removeItem(MEMBER_MANUAL_LOGOUT_STORAGE_KEY);
  emitMemberSessionChange();
}

export function clearMemberSession() {
  if (!canUseStorage()) {
    return;
  }

  localStorage.removeItem(MEMBER_ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(MEMBER_USER_STORAGE_KEY);
  localStorage.removeItem(MEMBER_DASHBOARD_STORAGE_KEY);
  emitMemberSessionChange();
}

export function markMemberSessionAsLoggedOut() {
  if (!canUseStorage()) {
    return;
  }

  localStorage.setItem(MEMBER_MANUAL_LOGOUT_STORAGE_KEY, "1");
  localStorage.removeItem(MEMBER_ACCESS_TOKEN_STORAGE_KEY);
  localStorage.removeItem(MEMBER_USER_STORAGE_KEY);
  localStorage.removeItem(MEMBER_DASHBOARD_STORAGE_KEY);
  emitMemberSessionChange();
}

export async function signOutMemberSession() {
  try {
    await logoutMemberSession();
  } catch (caughtError) {
    if (!isRefreshSessionError(caughtError)) {
      throw caughtError;
    }
  } finally {
    markMemberSessionAsLoggedOut();
  }
}

export async function restoreMemberAccessToken(): Promise<string | null> {
  if (hasManualLogoutMarker()) {
    return null;
  }

  const storedToken = readMemberAccessToken();

  if (storedToken && !isMemberAccessTokenExpired(storedToken)) {
    if (isMemberAccessTokenExpired(storedToken, 120)) {
      void refreshMemberSessionSilently();
    }

    return storedToken;
  }

  try {
    const refreshed = await refreshMemberSession();
    if (!isMemberUser(refreshed.user)) {
      markMemberSessionAsLoggedOut();
      return null;
    }
    persistMemberSession(refreshed.accessToken, refreshed.user);
    return refreshed.accessToken;
  } catch (caughtError) {
    if (isRefreshSessionError(caughtError)) {
      clearMemberSession();
      return null;
    }

    throw caughtError;
  }
}

export async function withMemberSession<T>(action: (accessToken: string) => Promise<T>): Promise<T | null> {
  if (hasManualLogoutMarker()) {
    return null;
  }

  const storedToken = readMemberAccessToken();

  if (storedToken && !isMemberAccessTokenExpired(storedToken)) {
    const shouldRenewSessionSoon = isMemberAccessTokenExpired(storedToken, 120);

    try {
      const result = await action(storedToken);

      if (shouldRenewSessionSoon) {
        void refreshMemberSessionSilently();
      }

      return result;
    } catch (caughtError) {
      if (!isUnauthorizedError(caughtError)) {
        throw caughtError;
      }
    }
  }

  try {
    const refreshed = await refreshMemberSession();
    if (!isMemberUser(refreshed.user)) {
      markMemberSessionAsLoggedOut();
      return null;
    }
    persistMemberSession(refreshed.accessToken, refreshed.user);
    return await action(refreshed.accessToken);
  } catch (caughtError) {
    if (isRefreshSessionError(caughtError) || isUnauthorizedError(caughtError)) {
      clearMemberSession();
      return null;
    }

    throw caughtError;
  }
}
